import { useEffect, type RefObject } from 'react';

interface UseKeyboardNavigationOptions {
  minimized: boolean;
  sessionsOpen: boolean;
  closeSessions: () => void;
  closeChat: () => void;
  chatContainerRef: RefObject<HTMLDivElement | null>;
  sessionsPanelRef: RefObject<HTMLDivElement | null>;
}

export function useKeyboardNavigation({
  minimized,
  sessionsOpen,
  closeSessions,
  closeChat,
  chatContainerRef,
  sessionsPanelRef,
}: UseKeyboardNavigationOptions): void {
  // Escape to close
  useEffect(() => {
    if (minimized) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (sessionsOpen) {
        closeSessions();
        return;
      }
      closeChat();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [minimized, closeChat, sessionsOpen, closeSessions]);

  // Focus trap: cycle focus within dialog when open
  useEffect(() => {
    if (minimized) return;
    const container = (sessionsOpen ? sessionsPanelRef.current : chatContainerRef.current) ?? null;
    if (!container) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const selector =
        'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([aria-hidden="true"])';
      let focusable: NodeListOf<HTMLElement>;
      try {
        focusable = container.querySelectorAll<HTMLElement>(selector);
      } catch {
        focusable = container.querySelectorAll<HTMLElement>(
          'button, a[href], input, select, textarea, [tabindex]'
        );
      }
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      // If focus is outside the container, pull it back to first element
      if (!container.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [minimized, sessionsOpen, chatContainerRef, sessionsPanelRef]);
}
